import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getToken } from '@shared/services/api';
import { Card, Button } from '@shared/components/ui';
import { UserPlus, CheckCircle2, XCircle, ArrowRight, Shield } from 'lucide-react';

export default function AcceptInvitation() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [invitationAccepted, setInvitationAccepted] = useState(false);
  const attemptedInvitationToken = useRef<string | null>(null);

  const authToken = getToken();

  useEffect(() => {
    if (!token) {
      attemptedInvitationToken.current = null;
      setMessage({ type: 'error', text: t('acceptInvitation.errors.invalidLink') });
      return;
    }
    if (authToken && attemptedInvitationToken.current !== token) {
      attemptedInvitationToken.current = token;
      acceptInvitation();
    }
  }, [token, authToken, t]);

  const acceptInvitation = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await fetch('/api/v1/invitations/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ token }),
      });

      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: data.message || t('acceptInvitation.welcome') });
        setInvitationAccepted(true);
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        const body = await response.text();
        let errorMessage = body.trim();
        if (errorMessage) {
          try {
            const error = JSON.parse(errorMessage);
            if (typeof error === 'string') {
              errorMessage = error;
            } else if (typeof error?.error === 'string') {
              errorMessage = error.error;
            } else if (typeof error?.message === 'string') {
              errorMessage = error.message;
            }
          } catch {
            // The API may return plain-text errors via http.Error.
          }
        }
        setMessage({
          type: 'error',
          text: errorMessage || t('acceptInvitation.errors.failed'),
        });
      }
    } catch {
      setMessage({ type: 'error', text: t('acceptInvitation.errors.network') });
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    sessionStorage.setItem('pending_invitation_token', token || '');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-surface-light dark:bg-surface-dark flex items-center justify-center p-4">
      <div className="max-w-md w-full animate-in fade-in zoom-in duration-500">
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-brand-600 rounded-2xl flex items-center justify-center mx-auto shadow-xl shadow-brand-200 -rotate-3">
            <UserPlus className="text-white dark:text-on-accent-dark w-8 h-8" strokeWidth={2.5} />
          </div>
          <h1 className="mt-6 text-3xl font-black text-slate-900 dark:text-white tracking-tighter uppercase">
            {t('acceptInvitation.title')}{' '}
            <span className="text-brand-600 dark:text-brand-400">
              {t('acceptInvitation.titleHighlight')}
            </span>
          </h1>
        </div>

        <Card className="p-10 text-center space-y-8 shadow-elevated">
          {message && (
            <div
              role={message.type === 'error' ? 'alert' : 'status'}
              className={`p-4 rounded-xl border font-bold flex items-center gap-3 ${
                message.type === 'success'
                  ? 'bg-state-ok-bg-light dark:bg-state-ok-bg-dark border-state-ok-light/20 dark:border-state-ok-dark/20 text-state-ok-light dark:text-state-ok-dark'
                  : 'bg-state-danger-bg-light dark:bg-state-danger-bg-dark border-state-danger-light/20 dark:border-state-danger-dark/20 text-state-danger-light dark:text-state-danger-dark'
              }`}
            >
              {message.type === 'success' ? <CheckCircle2 size={20} /> : <XCircle size={20} />}
              <span className="text-sm">{message.text}</span>
            </div>
          )}

          {!token && !loading && !invitationAccepted && (
            <Button
              className="min-h-11 w-full"
              onClick={() => navigate(authToken ? '/dashboard' : '/login')}
            >
              {authToken
                ? t('acceptInvitation.backToDashboard')
                : t('acceptInvitation.backToLogin')}
            </Button>
          )}

          {token && !authToken && !loading && !invitationAccepted && (
            <div className="space-y-8">
              <div className="space-y-2">
                <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                  {t('acceptInvitation.identityRequired')}
                </h2>
                <p className="text-text-muted-light dark:text-text-muted-dark font-medium">
                  {t('acceptInvitation.signInPrompt')}
                </p>
              </div>

              <div className="p-4 bg-surface-2-light dark:bg-surface-2-dark rounded-2xl border border-surface-border-light dark:border-surface-border-dark flex items-start gap-3 text-left">
                <Shield
                  className="text-text-subtle-light dark:text-text-subtle-dark shrink-0 mt-0.5"
                  size={18}
                />
                <p className="text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark leading-relaxed">
                  {t('acceptInvitation.autoLinkMessage')}
                </p>
              </div>

              <Button className="w-full h-14 text-lg" onClick={handleLogin}>
                {t('acceptInvitation.authenticateButton')} <ArrowRight size={20} className="ml-2" />
              </Button>
            </div>
          )}

          {loading && (
            <div className="py-8 space-y-4">
              <div className="animate-spin h-12 w-12 border-b-2 border-brand-600 rounded-full mx-auto"></div>
              <p className="text-text-subtle-light dark:text-text-subtle-dark font-black uppercase tracking-widest text-[10px]">
                {t('acceptInvitation.updatingAccess')}
              </p>
            </div>
          )}

          {invitationAccepted && (
            <div className="space-y-4 py-8">
              <div className="w-20 h-20 bg-state-ok-bg-light dark:bg-state-ok-bg-dark rounded-full flex items-center justify-center mx-auto text-state-ok-light dark:text-state-ok-dark">
                <CheckCircle2 size={48} />
              </div>
              <h2 className="text-2xl font-black text-slate-900 dark:text-white">
                {t('acceptInvitation.accessGranted')}
              </h2>
              <p className="text-text-muted-light dark:text-text-muted-dark font-medium">
                {t('acceptInvitation.provisioning')}
              </p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
